import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment';

import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  Popup,
} from 'react-leaflet';

import Leaflet from 'leaflet';

// Import custom SVG icons extension
import '../utils/svg-icons';

import {
  getWindowHeight,
  HEADER_HEIGHT,
  DRAWER_WIDTH,
  DRAWER_WIDTH_SMALL,
} from '../utils';
import { usNPPsSelector } from '../store/usNPPs';
import { getElapsedHoursInCurrentMonth } from '../utils/timeUtils';

const ICON_OPTIONS = {
  fillOpacity: 1,
  iconSize: [24, 38],
  circleColor: 'white',
  circleRatio: 0.6,
  circleWeight: 3,
};

function USNPPMap({
  currentUSNPPId = null,
  onUSNPPClick,
  drawerHeight,
  currentDate,
}) {
  // Use stable selector to avoid recreating objects
  const usNPPsData = useSelector(usNPPsSelector);
  const loads = useSelector(state => state.usNPPs?.loads || {});
  const productionByPlant = useSelector(
    state => state.usNPPs?.productionByPlant || {},
  );
  const loadedData = useSelector(state => state.usNPPs?.loadedData || {});

  // Memoize US NPPs to prevent unnecessary re-renders
  const usNPPs = useMemo(() => {
    if (!usNPPsData || usNPPsData.length === 0) {
      console.log('USNPPMap: No US NPPs data available');
      return [];
    }

    console.log('USNPPMap: Processing US NPPs data', {
      usNPPsData: usNPPsData,
    });

    return usNPPsData;
  }, [usNPPsData, currentUSNPPId]);

  const isSmallScreen = window.innerWidth <= 750;
  const height = getWindowHeight();
  const dispatch = useDispatch();
  const leftPosition = isSmallScreen
    ? Math.floor(window.innerWidth * 0.5)
    : DRAWER_WIDTH; // Use 50% width on small screens, fixed width on large screens

  // Debug logging
  console.log('USNPPMap debug:', {
    isSmallScreen,
    leftPosition,
    windowWidth: window.innerWidth,
    drawerHeight,
  });

  // Map relies on USNPPView to preload data. No data fetching here to avoid duplication.

  // Use last-month load rate strictly (ignore daily losses for pin fill)
  const computeLoadRate = plantId => {
    // Prefer store loads
    const entry = loads[String(plantId)] || {};
    if (typeof entry.monthlyLoadRate === 'number') return entry.monthlyLoadRate;
    if (typeof entry.loadRate === 'number') return entry.loadRate;
    // Fallback to per-plant monthly production series
    const y = moment(currentDate).year();
    const series = productionByPlant?.[y]?.[String(plantId)] || null;
    if (Array.isArray(series) && series.length > 0) {
      const last =
        [...series].reverse().find(m => Number(m.production) > 0) ||
        series[series.length - 1];
      const cap =
        Number(
          (usNPPs.find(p => String(p.id) === String(plantId)) || {}).capacityMW,
        ) || 0;
      const totalHours =
        moment(`${y}-${String(last.month).padStart(2, '0')}-01`).daysInMonth() *
        24;

      // For current month, use elapsed hours for more accurate load calculations
      const now = moment(currentDate);
      const isCurrentMonth = now.year() === y && now.month() + 1 === last.month;
      const effectiveHours = isCurrentMonth
        ? getElapsedHoursInCurrentMonth(y, last.month)
        : totalHours;

      if (cap > 0 && effectiveHours > 0)
        return Math.max(
          0,
          Math.min(1, Number(last.production || 0) / (cap * effectiveHours)),
        );
    }
    return 0;
  };
  const computeNotif = plantId => !!loads[String(plantId)]?.notif;

  const hoursInMonth = isoDate => {
    const d = moment(isoDate);
    return d.daysInMonth() * 24;
  };

  const formatEnergy = mwh => {
    const val = Number(mwh) || 0;
    if (val >= 1000) return `${Math.round(val / 1000)} GWh`;
    return `${Math.round(val)} MWh`;
  };
  const formatPower = mw => {
    const val = Number(mw) || 0;
    if (val >= 1000) return `${(val / 1000).toFixed(2)} GW`;
    return `${Math.round(val)} MW`;
  };

  // Memoize US NPP markers to prevent unnecessary re-creation
  const usNPPMarkers = useMemo(() => {
    return usNPPs.map(usNPP => (
      <Marker
        key={usNPP.id}
        title={usNPP.name}
        position={usNPP.coords}
        icon={
          usNPP.id === currentUSNPPId
            ? new Leaflet.DivIcon.SVGIcon.IndicatorIcon({
                ...ICON_OPTIONS,
                color: 'blue',
                loadRate: computeLoadRate(usNPP.id),
                notif: computeNotif(usNPP.id),
              })
            : new Leaflet.DivIcon.SVGIcon.IndicatorIcon({
                ...ICON_OPTIONS,
                loadRate: computeLoadRate(usNPP.id),
                notif: computeNotif(usNPP.id),
              })
        }
        eventHandlers={{
          click: () => onUSNPPClick(usNPP),
        }}
      >
        <Popup>
          {(() => {
            const cap = Number(usNPP.capacityMW) || 0;
            const y = moment(currentDate).year();
            const entry = loads[String(usNPP.id)] || {};
            const series = productionByPlant?.[y]?.[String(usNPP.id)] || [];
            const last =
              Array.isArray(series) && series.length > 0
                ? [...series].reverse().find(m => Number(m.production) > 0) ||
                  series[series.length - 1]
                : null;
            const monthNum = last ? Number(last.month) : null;
            const totalHm = monthNum
              ? moment(
                  `${y}-${String(monthNum).padStart(2, '0')}-01`,
                ).daysInMonth() * 24
              : Number(entry.hoursInMonth) || hoursInMonth(currentDate);

            // For current month, use elapsed hours for more accurate calculations
            const now = moment(currentDate);
            const isCurrentMonth =
              monthNum && now.year() === y && now.month() + 1 === monthNum;
            const hm =
              isCurrentMonth && last
                ? getElapsedHoursInCurrentMonth(y, monthNum)
                : totalHm;

            const energy = last
              ? Number(last.production)
              : entry.monthProdMWh != null
              ? Number(entry.monthProdMWh)
              : 0;
            const monthlyRate =
              cap > 0 && hm > 0 && energy != null
                ? Math.max(0, Math.min(1, energy / (cap * hm)))
                : 0;
            const avgPower = hm > 0 && energy != null ? energy / hm : 0;
            const monthLoad =
              monthlyRate != null ? Math.round(monthlyRate * 100) : 0;

            // Debug logging for suspicious states
            if ((energy === 0 || avgPower === 0) && monthLoad >= 90) {
              // eslint-disable-next-line no-console
              console.debug('[USNPPMap] Inconsistent popup values', {
                plantId: usNPP.id,
                name: usNPP.name,
                cap,
                hm,
                entry,
                monthlyRate,
                energy,
                avgPower,
                monthLoad,
                date: currentDate,
              });
            }
            return (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {usNPP.name}
                </div>
                <div>Current month power: {formatPower(avgPower)}</div>
                <div>Current month load: {monthLoad}%</div>
                <div>Current month production: {formatEnergy(energy)}</div>
              </div>
            );
          })()}
        </Popup>
      </Marker>
    ));
  }, [usNPPs, currentUSNPPId, loads, currentDate]);

  return (
    <MapContainer
      center={[39.8283, -98.5795]} // Center of the US
      zoom={isSmallScreen ? 4 : 5}
      style={{
        position: 'fixed',
        top: HEADER_HEIGHT,
        right: 0,
        bottom: 0,
        left: isSmallScreen ? '50vw' : leftPosition, // Use CSS unit on small screens, JS calculation on large screens
        marginTop: 0,
        zIndex: 1, // Ensure map is above other content but below header
        width: isSmallScreen ? '50vw' : 'auto', // Set width only on small screens
      }}
      zoomControl={false}
      className="map-custom-cursor"
    >
      <ZoomControl position="bottomright" />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      />
      {usNPPMarkers}
    </MapContainer>
  );
}

USNPPMap.propTypes = {
  currentUSNPPId: PropTypes.string,
  onUSNPPClick: PropTypes.func.isRequired,
  drawerHeight: PropTypes.number.isRequired,
  currentDate: PropTypes.string.isRequired,
};

export default USNPPMap;
